import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import styles from './PlayablesCatalogMobileDrawer.module.css';
import { 
  playables, 
  inputables, 
  getPlayableById, 
  getInputableById,
  getFilteredPlayables,
  type Playable,
  type Inputable,
  type PlayableResult,
  type InputableResult
} from './catalog';

interface PlayablesCatalogMobileDrawerProps {
  onPlayableSelect: (playable: PlayableResult | null) => void;
  onInputableSelect: (inputable: InputableResult | null) => void;
  setShowPlayablesPopup: (show: boolean) => void;
  isVisible: boolean;
  characterDisplayName?: string;
}

const PlayablesCatalogMobileDrawer: React.FC<PlayablesCatalogMobileDrawerProps> = ({
  onPlayableSelect,
  onInputableSelect,
  setShowPlayablesPopup,
  isVisible,
  characterDisplayName = 'your character'
}) => {
  const [isAnimatedIn, setIsAnimatedIn] = useState<boolean>(false);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragOffset, setDragOffset] = useState<number>(0);
  const drawerRef = useRef<HTMLDivElement>(null);
  const touchStartY = useRef<number>(0);
  const currentDragY = useRef<number>(0);
  
  // Get filtered playables using the shared function
  const filteredPlayables = useMemo(() => {
    return getFilteredPlayables();
  }, []);
  
  // Animation effect when component mounts
  useEffect(() => {
    if (isVisible) {
      // Small delay to ensure DOM is ready for the animation
      const timer = setTimeout(() => {
        setIsAnimatedIn(true);
      }, 10);
      
      // Prevent body scrolling when drawer is open
      document.body.style.overflow = 'hidden';
      
      return () => {
        clearTimeout(timer);
      };
    }
  }, [isVisible]);
  
  // Handle close with animation
  const handleClose = (): void => {
    setIsAnimatedIn(false);
    // Wait for animation to complete before unmounting
    setTimeout(() => {
      document.body.style.overflow = '';
      setShowPlayablesPopup(false);
      // Reset drag state
      setDragOffset(0);
    }, 300);
  };

  const handlePlayableClick = (playable: Playable): void => {
    if (playable.component || playable.behaviorType === 'background') {
      onPlayableSelect(getPlayableById(playable.id));
      handleClose();
    }
  };

  const handleInputableClick = (inputable: Inputable): void => {
    onInputableSelect(getInputableById(inputable.id));
    handleClose();
  };

  // Touch event handlers for drag-to-dismiss
  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>): void => {
    // Only allow dragging from the header/handle
    if (e.target instanceof Element && 
       (e.target.closest(`.${styles.header}`) || e.target.closest(`.${styles.handle}`))) {
      touchStartY.current = e.touches[0].clientY;
      currentDragY.current = e.touches[0].clientY;
      setIsDragging(true);
    }
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>): void => {
    if (!isDragging) return;
    
    const currentY = e.touches[0].clientY;
    const deltaY = currentY - touchStartY.current;
    
    // Only allow dragging downward
    if (deltaY > 0) {
      currentDragY.current = currentY;
      setDragOffset(deltaY);
      
      // Prevent scrolling while dragging
      e.preventDefault();
    }
  };

  const handleTouchEnd = (): void => {
    if (!isDragging) return;
    
    setIsDragging(false);
    
    // If dragged more than 100px or with enough velocity, close the drawer
    const velocity = currentDragY.current - touchStartY.current;
    if (dragOffset > 100 || velocity > 50) {
      handleClose();
    } else {
      // Reset position
      setDragOffset(0);
    }
  };

  // Mouse event handlers for desktop testing
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>): void => {
    // Only allow dragging from the header/handle
    if (e.target instanceof Element && 
       (e.target.closest(`.${styles.header}`) || e.target.closest(`.${styles.handle}`))) {
      touchStartY.current = e.clientY;
      currentDragY.current = e.clientY;
      setIsDragging(true);
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (!isDragging) return;
    
    const currentY = e.clientY;
    const deltaY = currentY - touchStartY.current;
    
    // Only allow dragging downward
    if (deltaY > 0) {
      currentDragY.current = currentY;
      setDragOffset(deltaY);
    }
  };

  const handleMouseUp = (): void => {
    if (!isDragging) return;
    
    setIsDragging(false);
    
    // If dragged more than 100px, close the drawer
    if (dragOffset > 100) {
      handleClose();
    } else {
      // Reset position
      setDragOffset(0);
    }
  };

  if (!isVisible) return null;

  // Calculate transform style based on drag
  const drawerStyle = {
    transform: isDragging ? `translateY(${dragOffset}px)` : '',
    transition: isDragging ? 'none' : 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
  };

  return createPortal(
    <div 
      className={`${styles.backdrop} ${isAnimatedIn ? styles.backdropVisible : ''}`}
      onClick={handleClose}
    >
      <div 
        ref={drawerRef}
        className={`${styles.drawerContainer} ${isAnimatedIn ? styles.visible : ''}`}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        style={drawerStyle}
      >
        <div className={styles.header}>
          <div className={styles.handle}></div>
        </div>
        
        <div className={styles.content}>
          {/* Tools Section - Horizontal Scrolling */}
          <div className={styles.toolsSection}>
            <h4 className={styles.sectionTitle}>Tools</h4>
            <div className={styles.toolsScrollContainer}>
              {inputables.map((inputable: Inputable) => (
                <div 
                  key={inputable.id}
                  className={styles.toolCard}
                  onClick={() => handleInputableClick(inputable)}
                >
                  <div className={styles.toolIconContainer}>
                    <img src={inputable.icon} alt="" />
                  </div>
                  <div className={styles.toolTitle}>{inputable.title}</div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Playables Section - More Prominent */}
          <div className={styles.playablesSection}>
            <h4 className={styles.sectionTitle}>Activities</h4>
            {filteredPlayables.map((playable: Playable) => (
              <div 
                key={playable.id} 
                className={styles.playableContainer}
                onClick={() => handlePlayableClick(playable)}
              >
                <div className={styles.playableBanner}>
                  <img src={playable.image} alt={playable.title} className={styles.playableBannerImage} />
                </div>
                <div className={styles.playableInfo}>
                  <div className={styles.playableIconContainer}>
                    <img src={playable.icon || playable.image} alt="" className={styles.playableIcon} />
                  </div>
                  <div className={styles.playableDetails}>
                    <div className={styles.playableTitle}>{playable.title}</div>
                    <div className={styles.playableDescription}>
                      {playable.description 
                        ? playable.description.replace('{{char}}', characterDisplayName)
                        : `Play ${playable.title} with ${characterDisplayName}`}
                    </div>
                  </div>
                  <div className={styles.playableArrow}>›</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PlayablesCatalogMobileDrawer; 